/** ScreenManager — 3 つの DOM スクリーンを切り替える簡易管理。
 *  Reference: docs/CLAUDE_CODE_UI_IMPLEMENTATION_BRIEF.md §7
 *  各 screen は <div class="screen" data-screen="..."></div>。
 *  active な 1 つだけ .is-active クラスを持ち、CSS で display 切替。
 */

import type { ScreenId } from './theme';

export class ScreenManager {
  private readonly roots: Record<ScreenId, HTMLDivElement>;
  private current: ScreenId | null = null;

  constructor() {
    const top = document.querySelector<HTMLDivElement>('[data-screen="top"]');
    const home = document.querySelector<HTMLDivElement>('[data-screen="home"]');
    const gameplay = document.querySelector<HTMLDivElement>('[data-screen="gameplay"]');
    if (!top || !home || !gameplay) {
      throw new Error(
        'ScreenManager: missing screen root(s). Expected three <div data-screen="..."> in index.html.',
      );
    }
    this.roots = { top, home, gameplay };
  }

  getRoot(id: ScreenId): HTMLDivElement {
    return this.roots[id];
  }

  /** 画面を切り替え、ブラウザ履歴に pushState する(M3)。
   *  初回呼び出し(current === null)は replaceState で初期エントリを上書きする。
   *  popstate 経由で呼ぶ場合は showWithoutHistory() を使うこと。 */
  show(id: ScreenId): void {
    if (this.current === id) return;
    if (this.current === null) {
      // 初回: replace して初期エントリを作る(履歴を余分に積まない)
      history.replaceState({ screen: id }, '');
    } else {
      history.pushState({ screen: id }, '');
    }
    this._applyScreen(id);
    this.current = id;
  }

  /** M3: popstate 経由の復元用。DOM だけ切り替え、履歴は積まない。 */
  showWithoutHistory(id: ScreenId): void {
    if (this.current === id) return;
    this._applyScreen(id);
    this.current = id;
  }

  getCurrent(): ScreenId | null {
    return this.current;
  }

  private _applyScreen(id: ScreenId): void {
    for (const key of Object.keys(this.roots) as ScreenId[]) {
      const root = this.roots[key];
      if (key === id) {
        root.classList.add('is-active');
        root.setAttribute('aria-hidden', 'false');
      } else {
        root.classList.remove('is-active');
        root.setAttribute('aria-hidden', 'true');
      }
    }
  }
}
